import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { AdvisoryLightbox } from '../../lightbox/advisorylightbox';
import { twoColumnLayout, heading, leftColumn, rightColumn, imgContainerlightBox, border, sectionMandatPad, artistHeadingStyle } from '../../lightbox/advisorylightbox.module.css';
import { formContainer, formSubmitButton, countryBorder, feildFlex, inputGroup } from '../../forms/vehicle-forms.module.css';
import { FormControl } from '../inputs/formControl';
import 'react-phone-number-input/style.css';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PropTypes from 'prop-types';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import { useSeekaConverge, useSeekaAnonId } from '@seeka-labs/converge-react';
import { v4 as uuidv4 } from 'uuid';
import { getUTMParameters } from '../../../utils/utmUtils';

const CountrySelect = ({ value, onChange, labels, selectedCode, ...rest }) => {
    const topCountries = ['SG', 'AU', 'US', 'GB', 'HK', 'MY', 'ID'];

    return (
        <div>
            <select
                {...rest}
                value={value || ''}
                onChange={(event) => {
                    const selectedValue = event.target.value || undefined;
                    onChange(selectedValue);
                }}
            >
                <option value="">International</option>
                {topCountries.map((country) => (
                    <option key={country} value={country}>
                        {labels[country]} +{getCountryCallingCode(country)}
                    </option>
                ))}
                {getCountries().map((country) => {
                    if (!topCountries.includes(country)) {
                        return (
                            <option key={country} value={country}>
                                {labels[country]} +{getCountryCallingCode(country)}
                            </option>
                        );
                    }
                    return null;
                })}
            </select>
        </div>
    );
};

CountrySelect.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    labels: PropTypes.objectOf(PropTypes.string).isRequired,
    selectedCode: PropTypes.string,
};

const generateUniqueId = () => {
    const uniqueNum = uuidv4();
    return `unique-form-submission-${uniqueNum}`;
};

const FreeAdvisoryGuideForm = ({ artworkName, vendorName, skuCode }) => {
    const methods = useForm({
        mode: 'all',
        reValidateMode: 'onBlur',
        defaultValues: {
            firstName: '',
            lastName: '',
            fullName: '',
            email: '',
            countryCode: '',
            phoneNumber: '',
            artworkName: artworkName,
            skuCode: skuCode,
            vendorName: vendorName,
            pageUrl: '',
            UTMSource: '',
            UTMMedium: '',
            UTMCampaign: '',
        }
    });

    const recapcha = useGoogleReCaptcha();

    const { register, handleSubmit, reset, formState: { errors, isValid, isDirty } } = methods;
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const anonId = useSeekaAnonId();
    const converge = useSeekaConverge();

    const onSubmit = async data => {
        const userAgent = navigator.userAgent;
        const countryCode = methods.watch('countryCode');
        const countryCallingCode = getCountryCallingCode(countryCode);
        const phoneNumber = methods.watch('phoneNumber');

        const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');

        if (cleanedPhoneNumber && !cleanedPhoneNumber.startsWith(countryCallingCode)) {
            data.phoneNumber = `+${countryCallingCode} ${cleanedPhoneNumber.replace(/^0/, '')}`;
        } else {
            data.phoneNumber = `+${cleanedPhoneNumber.replace(/^0/, '')}`;
        }

        // Retrieve UTM parameters if not already set in data
        if (!data.UTMSource) {
            const utmSource = sessionStorage.getItem('utm_source');
            if (utmSource) {
                data.UTMSource = utmSource;
            }
        }
        if (!data.UTMMedium) {
            const utmMedium = sessionStorage.getItem('utm_medium');
            if (utmMedium) {
                data.UTMMedium = utmMedium;
            }
        }
        if (!data.UTMCampaign) {
            const utmCampaign = sessionStorage.getItem('utm_campaign');
            if (utmCampaign) {
                data.UTMCampaign = utmCampaign;
            }
        }

        const pageUrl = sessionStorage.getItem('page_url');
        if (pageUrl) {
            data.pageUrl = pageUrl;
        }

        data.anonId = anonId;
        data.uniqueID = generateUniqueId();
        data.userAgent = userAgent;

        setSubmitting(true)
        setError('')
        setSuccess(false)

        const onError = (errors, message) => {
            setError(message || 'Failed to submit. Please try again later.')
        }

        try {
            const response = await fetch("/api/form/advisoryform", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    data: {
                        ...data,
                        fullName: `${data.firstName} ${data.lastName}`,
                    },
                    artworkName: artworkName,
                    vendorName: vendorName,
                    skuCode: skuCode,
                    recaptchaToken: await recapcha.executeRecaptcha(),
                    SeekaID: anonId,
                    uniqueId: data.uniqueID,
                    userAgent: data.userAgent,
                })
                //console.log('API response:', response),
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setSuccess(true);
                    reset();

                    converge.identity.mergeProfile({
                        firstName: [data.firstName],
                        lastName: [data.lastName],
                        email: [data.email],
                        phone: [data.phoneNumber],
                        address: [{
                            countryCode: data.countryCode,
                        }],
                    })
                        .finally(async () => {
                            await converge.track.lead({
                                sourceContentName: 'Advisory Form',
                            });
                        });
                } else {
                    onError(null, result.errorMessage);
                }
            } else {
                onError();
            }
        } catch (err) {
            onError(err);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (anonId) {
            // Proceed with using anonId, such as tracking events or identities
        } else {
            // Anonymous ID is undefined
        }
    }, [anonId]);

    useEffect(() => {
        getUTMParameters(methods);
    }, [methods]);

    const [selectedCode, setSelectedCode] = useState('');

    return (
        <FormProvider {...methods}>
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                method="POST"
                name="contact"
                autoComplete="off"
            >
                <div className={formContainer}>
                    <div style={{ display: 'none' }}><FormControl {...register("honeyPot", { required: false })} /></div>
                    <div className={feildFlex}>
                        <div className={inputGroup}>
                            <label htmlFor="firstName">First Name*</label>
                            <FormControl options={{ required: true }} name="firstName" error={errors.firstName} />
                        </div>
                        <div className={inputGroup}>
                            <label htmlFor="lastName">Last Name*</label>
                            <FormControl options={{ required: true }} name="lastName" error={errors.lastName} />
                        </div>
                    </div>
                    <label htmlFor="email">Email Address*</label>
                    <FormControl options={{
                        required: true, pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: "Invalid email",
                        }
                    }} name="email" error={errors.email} />
                    <label htmlFor="countryCode">Enter your Country Code*</label>
                    <div className={countryBorder}>
                        <CountrySelect
                            labels={en}
                            name="countryCode"
                            defaultCountry="SG"
                            value={methods.watch('countryCode')}
                            onChange={(value) => {
                                methods.setValue('countryCode', value, { shouldValidate: true });
                                setSelectedCode(value ? `+${getCountryCallingCode(value)}` : '');
                            }}
                            selectedCode={selectedCode}
                        />
                    </div>
                    <label htmlFor="phoneNumber">Phone*</label>
                    <FormControl
                        options={{
                            required: true,
                            pattern: {
                                value: /^[^\s]+$/,
                                message: 'Spaces are not allowed',
                            },
                            validate: {
                                noCountryCode: value => {
                                    const countryCode = methods.watch('countryCode');
                                    const countryCallingCode = getCountryCallingCode(countryCode);
                                    const phoneNumber = value.replace(/\s/g, '');

                                    if (phoneNumber.startsWith(`+${countryCallingCode}0`)) {
                                        return 'Please do not add the country code or a leading zero (0) to the phone number.';
                                    } else if (phoneNumber.startsWith(`+${countryCallingCode}`)) {
                                        return 'Please do not add the country code manually.';
                                    } else if (phoneNumber.startsWith(`0`)) {
                                        return 'Please do not add a leading zero (0) to the phone number.';
                                    } else if (phoneNumber.startsWith(`${countryCallingCode}`) && !phoneNumber.startsWith(`+${countryCallingCode}`)) {
                                        return 'Please do not add the country code and just enter the phone number.';
                                    } else if (/[^0-9]/.test(phoneNumber)) {
                                        return 'Please do not include special characters in the phone number.';
                                    }

                                    return true;
                                },
                            },
                        }}
                        type="tel"
                        inputMode="tel"
                        name="phoneNumber"
                        error={errors.phoneNumber}
                        value={`${selectedCode} ${methods.watch('phoneNumber')}`}
                        onChange={(e) => {
                            const cleanedPhoneNumber = e.target.value.replace(/\s/g, '');
                            methods.setValue('phoneNumber', cleanedPhoneNumber, { shouldValidate: true });
                        }}
                    />
                    {error && <div style={{ color: 'white', backgroundColor: 'red', borderRadius: '8px', padding: '2px 4px', fontSize: '11px', marginBottom: '15px' }}>{error}</div>}
                    {success && <div style={{ color: 'white', backgroundColor: 'green', borderRadius: '8px', padding: '2px 4px', fontSize: '11px', marginBottom: '15px' }}>Sent!</div>}
                    <button type="submit" disabled={!isValid || submitting} className={formSubmitButton}>
                        Submit Enquiry
                    </button>
                </div>
            </form>
        </FormProvider>
    )
}

export const AdvisoryFormLightbox = ({ productImage, altText, artworkName, vendorName, skuCode }) => {
    return (
        <AdvisoryLightbox title="DOWNLOAD THE GUIDE">
            <div>
                <h1 className={heading}>Art Works Advisory Enquiry</h1>
                <p className={sectionMandatPad}>We'd be delighted to assist you with your investment in the exciting artwork market. One of our team will respond to you within 1 business day once you submit the form below.</p>
            </div>
            <div className={twoColumnLayout}>
                <div className={leftColumn}>
                    <GoogleReCaptchaProvider
                        reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
                        scriptProps={{
                            async: false,
                            defer: false,
                            appendTo: 'head',
                            nonce: undefined,
                            onLoad: () => {
                                // console.log('reCAPTCHA script loaded');
                            },
                        }}
                    >
                        <FreeAdvisoryGuideForm artworkName={artworkName} vendorName={vendorName} skuCode={skuCode} />
                    </GoogleReCaptchaProvider>
                </div>
                <div className={rightColumn}>
                    <div className={border}>
                        <img src={productImage} alt={altText || artworkName} className={imgContainerlightBox} />
                        <p className={artistHeadingStyle}>{artworkName}</p>
                        <span>{vendorName}</span>
                    </div>
                </div>
            </div>
        </AdvisoryLightbox>
    )
}
